// src/components/studio/steps/Step3Photo.tsx
import { motion } from "framer-motion";
import { Upload, X } from "lucide-react";
import { tiktokPixelService } from "../../../services/tiktok-pixel.service";

interface Step3PhotoProps {
  customization: any;
  setCustomization: (c: any) => void;
  validation: any;
}

export default function Step3Photo({
  customization,
  setCustomization,
  validation,
}: Step3PhotoProps) {
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () =>
        setCustomization({
          ...customization,
          recipientImage: reader.result,
        });
      reader.readAsDataURL(file);

      tiktokPixelService.track(
        "AddToWishlist",
        {
          contents: [
            {
              content_id: "recipient_photo",
              content_type: "product",
              content_name: "Recipient Photo Upload",
            },
          ],
          value: 0,
          currency: "KES",
        },
        "wishlist:recipient_photo"
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex h-full min-h-0 flex-col overflow-hidden"
    >
      <div className="min-h-0 flex-1 mb-3 rounded-2xl border-2 border-dashed border-white/20 relative overflow-hidden group">
        {customization.recipientImage ? (
          <>
            {/* Main image */}
            <img
              src={customization.recipientImage}
              className="w-full h-full object-cover"
              alt="Preview"
            />

            {/* Portrait overlay on the right side */}
            <div className="absolute right-0 top-0 bottom-0 w-1/3 pointer-events-none">
              {/* Gradient overlay for smooth transition */}
              <div className="absolute inset-0 bg-gradient-to-l from-black/60 via-black/30 to-transparent" />

              {/* Portrait frame RIGHT */}
              <div className="hidden md:block absolute right-4 top-1/2 -translate-y-1/2 w-34 h-44 md:w-[20vw] md:h-[50vh] rounded-2xl overflow-hidden border-2 border-white/30 shadow-2xl transform rotate-3">
                <img
                  src={customization.recipientImage}
                  className="w-full h-full object-cover"
                  alt="Portrait Preview"
                />
              </div>

              {/* Portrait frame LEFT */}
              <div className="hidden md:block absolute left-[-52vw] top-1/2 -translate-y-1/2 w-24 h-32 md:w-[20vw] md:h-[50vh] rounded-2xl overflow-hidden border-2 border-white/30 shadow-2xl transform rotate-3">
                <img
                  src={customization.recipientImage}
                  className="w-full h-full object-cover"
                  alt="Portrait Preview"
                />
              </div>

              {/* Optional: Add a second smaller portrait for more visual interest */}
              <div className="hidden md:block absolute right-12 bottom-4 w-16 h-20 rounded-xl overflow-hidden border border-white/20 shadow-lg transform -rotate-6 opacity-80">
                <img
                  src={customization.recipientImage}
                  className="w-full h-full object-cover"
                  alt="Portrait Preview Small"
                />
              </div>
            </div>

            {/* Delete button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCustomization({
                  ...customization,
                  recipientImage: null,
                });
              }}
              className="absolute top-3 right-3 p-2 bg-red-500/80 rounded-xl z-10"
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-colors">
            <Upload className="text-white/20 mb-2" size={24} />
            <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">
              Select Memory *
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
        )}
      </div>
      {/* <PrivacyTrustNote variant="photo" /> */}
      {!validation.isStep3Valid && (
        <p className="text-[10px] text-red-400 mt-2">* Please upload a photo</p>
      )}
    </motion.div>
  );
}
